import { useEffect, useState } from "react";
import { Kart } from "@koclukpanel/ui";
import {
  gorusmelerim,
  kendiKaydim,
  veliYaklasanGorusmeler,
  type KendiGorusmem,
  type VeliYaklasanGorusme,
} from "./api";

/**
 * YAKLAŞAN GÖRÜŞMELER + "GÖRÜŞMEYE KATIL" — F-SES-02b (Emre 06.10.2026)
 *
 * Bağlantının kime görüneceği SUNUCUDA: öğrenci kendi görüşmesinde görür,
 * veli yalnız VELİ görüşmesinde (birebir koçluk görüşmesinde `meetUrl`
 * `null` gelir). Bu bileşen yalnız gelen bağlantıyı çizer — istemci
 * bağlantıyı "gizlemeye" çalışmıyor; gizlenecek olan zaten gelmiyor.
 *
 * Hata/yükleme sessiz: kart ek bir kolaylık, takvim ekranının asıl işi
 * değil; görüşme alınamazsa ekranın geri kalanı bozulmamalı.
 */

const TUR_ADI: Record<string, string> = {
  COACHING: "Koçluk görüşmesi",
  PARENT_MEETING: "Veli görüşmesi",
  MAKEUP: "Telafi görüşmesi",
  INTRO: "Tanışma görüşmesi",
};

/** Planlı/hatırlatılmış, başlangıcı 3 saatten eski değil — sunucudaki `yaklasanGorusmeMi` ile aynı. */
function yaklasanMi(g: { status?: string; scheduledAt: string }, simdi: Date): boolean {
  if (g.status !== undefined && g.status !== "SCHEDULED" && g.status !== "REMINDED") return false;
  return new Date(g.scheduledAt).getTime() >= simdi.getTime() - 3 * 3_600_000;
}

function zaman(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    timeZone: "Europe/Istanbul",
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type Satir = Pick<KendiGorusmem, "id" | "kind" | "scheduledAt" | "meetUrl">;

function Liste({ satirlar }: { satirlar: readonly Satir[] }) {
  if (satirlar.length === 0) return null;
  return (
    <Kart baslik="Yaklaşan görüşmeler">
      <ul className="flex flex-col gap-3">
        {satirlar.map((g) => (
          <li key={g.id} className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm">
              <p className="text-notr-900 font-medium">{TUR_ADI[g.kind] ?? "Görüşme"}</p>
              <p className="text-notr-600">{zaman(g.scheduledAt)}</p>
            </div>
            {g.meetUrl && (
              <a
                href={g.meetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-alan bg-marka-600 text-notr-0 inline-flex min-h-11 items-center px-4 text-sm font-medium"
              >
                Görüşmeye katıl
              </a>
            )}
          </li>
        ))}
      </ul>
    </Kart>
  );
}

/** Öğrencinin kendi yaklaşan görüşmeleri. */
export function YaklasanGorusmelerim() {
  const [satirlar, setSatirlar] = useState<Satir[]>([]);
  useEffect(() => {
    let iptal = false;
    void (async () => {
      try {
        const ben = await kendiKaydim();
        const liste = await gorusmelerim(ben.id);
        const simdi = new Date();
        const yakin = liste
          .filter((g) => yaklasanMi(g, simdi))
          .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
          .slice(0, 5);
        if (!iptal) setSatirlar(yakin);
      } catch {
        // Sessiz — bkz. dosya başı.
      }
    })();
    return () => {
      iptal = true;
    };
  }, []);
  return <Liste satirlar={satirlar} />;
}

/** Velinin, çocuğunun yaklaşan görüşmeleri — bağlantı yalnız veli görüşmesinde gelir. */
export function VeliYaklasanGorusmeleri({ ogrenciId }: { ogrenciId: string }) {
  const [satirlar, setSatirlar] = useState<VeliYaklasanGorusme[]>([]);
  useEffect(() => {
    let iptal = false;
    void veliYaklasanGorusmeler(ogrenciId)
      .then((l) => {
        if (!iptal) setSatirlar(l.slice(0, 5));
      })
      .catch(() => undefined);
    return () => {
      iptal = true;
    };
  }, [ogrenciId]);
  return <Liste satirlar={satirlar} />;
}
